import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';

export const groupColors = {
  Periodic: "bg-Periodic",
  Humanitarian: "bg-Humanitarian",
  PreventWaste: "bg-PreventWaste",
  AtomEconomy: "bg-AtomEconomy",
  LessHazardous: "bg-LessHazardous",
  MolecularDesign: "bg-MolecularDesign",
  Solvents: "bg-Solvents",
  Energy: "bg-Energy",
  Renewable: "bg-Renewable",
  Catalysis: "bg-Catalysis",
  Degradation: "bg-Degradation",
  Measurement: "bg-Measurement",
  Frameworks: "bg-Frameworks",
  Market: "bg-Market",
  Metrics: "bg-Metrics",
  Policie: "bg-Policie",
  Tools: "bg-Tools",
  Noble: "bg-Noble",
};


const containerVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2,
    },
  },
};

const elementVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};


export function handleElementClick(element, setSelectedElement) {
  setSelectedElement(element);
}

export function renderElementVideo(element, setSelectedElement, t, navigate) {
  return (
    <section className="flex flex-col min-h-screen w-full items-center px-4 pt-28 md:pt-32 pb-8">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Card Principal */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 md:p-10 mb-8 flex flex-col items-center max-w-4xl w-full"
      >
        {/* 1. Nome do Elemento */}
        <h2 className="text-xl md:text-2xl text-text-primary font-bold text-center mb-4">
          {t(element.description)}
        </h2>

        {/* 2. Botão de Voltar  */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedElement(null)}
            className="btn-primary inline-flex items-center px-6 py-2.5 text-sm md:text-base"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t("elements.back")}
          </motion.button>
        </motion.div>

        {/* 3. Vídeo */}
        <div className="relative overflow-hidden rounded-xl border border-glass-border shadow-card w-full aspect-video">
          <iframe
            className="w-full h-full"
            src={element.video}
            title={element.label}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center"
      >
        <h1 className="text-xl text-text-primary font-bold mb-4">
          {t('TpqvsLibras.changeHome')}
        </h1>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="btn-secondary"
        >
          {t('TpqvsLibras.title')}
        </motion.button>
      </motion.div>
    </section>
  );
}

export function Element({
  element,
  handleElementClick,
  setSelectedElement,
  individual,
  groupsElements,
  greenChemistry,
  enablingSystem,
  tpqvs,
  windowSize,
}) {
  const { t } = useTranslation();
  const elementColor = groupColors[element.group];
  const commonClasses = "flex flex-col justify-center items-center text-black transition-all duration-300 transform hover:scale-105 hover:shadow-lg relative cursor-pointer border border-black/20 rounded-sm";

  const getSize = () => {

    const width = windowSize.width;


    // Breakpoints responsivos
    if (width < 480) return { width: "90px", height: "92px" };
    if (width < 768) return { width: "95px", height: "95px" };
    if (width < 1024) return { width: "100px", height: "100px" };
    if (width < 1280) return { width: "100px", height: "103px" };
    if (width < 1460) return { width: "74px", height: "80px" };
    if (width < 1550) return { width: "80px", height: "86px" };
    if (width < 1650) return { width: "84px", height: "90px" };
    if (width < 1760) return { width: "92px", height: "99px" };
    if (width < 1890) return { width: "97px", height: "104px" };
    if (width < 2100) return { width: "105px", height: "115px" };
    if (width < 2200) return { width: "120px", height: "126px" }
    return { width: "140px", height: "146px" };
  };

  const size = getSize();

  const getTextClasses = () => {
    const width = windowSize.width;

    //Estilos para o conteudo de cada elemento, dependendo do tamanho da tela 
    if (width < 800 && tpqvs) {
      return {
        id: "text-[9px]",
        label: "text-[9px]",
        description: "text-[9px]"
      }
    } else if (width < 800) {
      return {
        id: "text-[4px]",
        label: "text-[5px]",
        description: "text-[4px]"
      }
    }
    if (individual) {
      return {
        id: "text-[11px]",
        label: "text-[12px]",
        description: "text-[10px]"
      }
    }
    if (width < 480 && enablingSystem) {
      return {
        id: "text-[9px]",
        label: "text-[10px]",
        description: "text-[9px]"
      }
    }
    if (width < 1024) {
      return {
        id: "text-[9px]",
        label: "text-[11px]",
        description: "text-[10px]"
      };
    }
    if (width < 1280) {
      return {
        id: "text-[8px]",
        label: "text-[8px]",
        description: "text-[7px]"
      };
    }
    if (width < 1460) {
      return {
        id: "text-[9px]",
        label: "text-[10px]",
        description: "text-[7px]"
      };
    }
    if (width < 1550) {
      return {
        id: "text-[9px]",
        label: "text-[9px]",
        description: "text-[8px]"
      };
    }
    if (width < 1650) {
      return {
        id: "text-[9px]",
        label: "text-[10px]",
        description: "text-[8px]"
      };
    }
    if (width < 1760) {
      return {
        id: "text-[10px]",
        label: "text-[11px]",
        description: "text-[9px]"
      };
    }
    if (width > 1760 && width < 2100) {
      return {
        id: "text-[11px]",
        label: "text-[12px]",
        description: "text-[10px]"
      };
    }
    if (width > 2100 && width < 2200) {
      return {
        id: "text-[12px]",
        label: "text-[13px]",
        description: "text-[13px]"
      };
    } if (width > 2200) {
      return {
        id: "text-[12px]",
        label: "text-[13px]",
        description: "text-[15px]"
      };
    }
    return {
      id: "text-[12px]",
      label: "text-[12px]",
      description: "text-[12px]"
    };
  };

  const textClasses = getTextClasses();

  return (
    <motion.section
      key={element.id}
      variants={elementVariants}
      initial="hidden"
      animate="visible"
    >
      <div
        className={`${elementColor} ${commonClasses}`}
        onClick={() => handleElementClick(element, setSelectedElement)}
        title={t(element.description)}
        style={{
          width: individual ? "110px" : groupsElements ? "38px" : greenChemistry ? "30px" : enablingSystem ? "80px" : size.width,
          height: individual ? "116px" : groupsElements ? "40px" : greenChemistry ? "40px" : enablingSystem ? "80px" : size.height,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className={`absolute top-0 left-1 ${textClasses.id}`}>
          {element.id}
        </div>
        <div className={`font-bold m-0 p-0 ${textClasses.label}`}>
          {element.label}
        </div>
        <div className={`m-0 p-0 break-words font-semibold ${textClasses.description}`}>
          {t(element.description)}
        </div>
      </div>
    </motion.section>
  );
};

export function renderColumn(
  elements,
  start,
  end,
  empty,
  quant,
  handleElementClick,
  setSelectedElement,
  individual,
  groupsElements,
  greenChemistry,
  enablingSystem,
  tpqvs,
  windowSize = { width: 1200 }
) {
  // Tratamento seguro para windowSize nao definido
  const safeWindowSize = windowSize || { width: 1200, height: 800 };

  const getEmptyBoxStyle = () => {

    const width = safeWindowSize.width;

    // Breakpoints responsivos da 'caixas vazias' (deve corresponder ao getSize )
    if (width < 480) {
      return { height: "92px" };
    } else if (width < 768) {
      return { height: "95px" };
    } else if (width < 1024) {
      return { height: "100px" };
    } else if (width < 1280) {
      return { height: "103px" };
    } else if (width < 1460) {
      return { height: "80px" };
    } else if (width < 1550) {
      return { height: "86px" };
    } else if (width < 1650) {
      return { height: "90px" };
    } else if (width < 1597) {
      return { height: "90px" };
    } else if (width < 1760) {
      return { height: "99px" }
    } else if (width < 1890) {
      return { height: "104px" };
    } else if (width < 2100) {
      return { height: "115px" };
    } else if (width < 2200) {
      return { height: "126px" }
    } else {
      return { height: "146px" };
    }
  };

  const emptyBoxStyle = getEmptyBoxStyle();

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      {empty && !enablingSystem && !individual && Array.from({ length: quant }).map((_, index) => (
        <div
          key={`empty-${index}`}
          style={emptyBoxStyle}
        />
      ))}
      {empty && individual && Array.from({ length: quant }).map((_, index) => (
        <div
          key={`individual-${index}`}
          style={{ height: "116px" }}
        />
      ))}
      {empty && enablingSystem && !individual && Array.from({ length: quant }).map((_, index) => (
        <div
          key={`humanitarian-${index}`}
          style={{ height: "80px" }}
        />
      ))}
      {elements.slice(start, end).map((element) => (
        <Element
          key={element.id}
          element={element}
          handleElementClick={handleElementClick}
          setSelectedElement={setSelectedElement}
          individual={individual}
          groupsElements={groupsElements}
          greenChemistry={greenChemistry}
          enablingSystem={enablingSystem}
          tpqvs={tpqvs}
          windowSize={safeWindowSize}
        />
      ))}
    </motion.div>
  );
}
